/**
 * Plan limits and usage for live captioning.
 *
 * Every captions response from core (`GET /captions`, `GET /captions/:id`,
 * `PUT /captions/:id`, `POST /captions/:id/start|stop|extend`) carries:
 *
 *   limits: { included, enforced, poolSec, maxLangs, maxConcurrent,
 *             maxWindowSec, periodStart, periodEnd, isTrial, billedBy? }
 *
 * `billedBy: 'provider'` marks a flow on the customer's own captioning
 * account: Castr's plan does not bill it, so the plan usage is not shown.
 *   usage:  { usedSec, remainingSec, runningFlows }
 *
 * `poolSec: null` is unlimited hours, `maxWindowSec: null` no per-window cap,
 * `maxConcurrent: null` no concurrency cap. `enforced: false` means core only
 * reports: the dashboard shows the numbers but blocks nothing. An older core
 * sends no `limits` at all; `normalizeLimits(undefined)` yields the defaults
 * below, which render exactly the pre-limits UI (no bar, every preset).
 */
import { DURATION_PRESETS, EXTEND_PRESETS, formatCountdown } from './captionLanguages';

export const DEFAULT_LIMITS = Object.freeze({
	included: true,
	enforced: false,
	poolSec: null,
	maxLangs: 4,
	maxConcurrent: null,
	maxWindowSec: null,
	periodStart: null,
	periodEnd: null,
	isTrial: false,
	billedBy: 'castr',
});

export const DEFAULT_USAGE = Object.freeze({ usedSec: 0, remainingSec: null, runningFlows: 0 });

/** Below this many seconds left, the pool counts as used up (nothing useful can start). */
export const POOL_EXHAUSTED_UNDER_SEC = 60;
/** Fraction of the pool used at which the dashboard shows the "upgrade for more hours" notice. */
export const POOL_NOTICE_RATIO = 0.8;

export const UPGRADE_ROUTE = '/subscribe';

const positiveOrNull = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' && Number(v) > 0 ? Number(v) : null);
const nonNegativeOr = (v, fallback) => (Number.isFinite(Number(v)) && v !== null && v !== '' && Number(v) >= 0 ? Number(v) : fallback);
const epochOrNull = (v) => {
	if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null;
	if (typeof v === 'string' && v) {
		const ms = Date.parse(v);
		return Number.isFinite(ms) ? ms : null;
	}
	return null;
};

/** Well-formed limits from core's payload, or the defaults when it sent none. */
export function normalizeLimits(raw) {
	if (!raw || typeof raw !== 'object') return { ...DEFAULT_LIMITS };
	const maxLangs = Number(raw.maxLangs);
	return {
		included: raw.included !== false,
		enforced: raw.enforced === true,
		poolSec: nonNegativeOr(raw.poolSec, null),
		maxLangs: Number.isInteger(maxLangs) && maxLangs > 0 ? maxLangs : DEFAULT_LIMITS.maxLangs,
		maxConcurrent: positiveOrNull(raw.maxConcurrent),
		maxWindowSec: positiveOrNull(raw.maxWindowSec),
		periodStart: epochOrNull(raw.periodStart),
		periodEnd: epochOrNull(raw.periodEnd),
		isTrial: raw.isTrial === true,
		billedBy: raw.billedBy === 'provider' ? 'provider' : 'castr',
	};
}

/** The customer's own captioning account pays for this flow: hide Castr's plan usage. */
export const billedByProvider = (limits) => limits?.billedBy === 'provider';

/**
 * Well-formed usage. `remainingSec` is derived from the pool when core omits
 * it, and stays `null` for an unlimited pool.
 */
export function normalizeUsage(raw, limits = DEFAULT_LIMITS) {
	const src = raw && typeof raw === 'object' ? raw : {};
	const usedSec = nonNegativeOr(src.usedSec, 0);
	let remainingSec = nonNegativeOr(src.remainingSec, null);
	if (remainingSec === null && Number.isFinite(limits?.poolSec) && limits.poolSec !== null) {
		remainingSec = Math.max(0, limits.poolSec - usedSec);
	}
	const runningFlows = Number(src.runningFlows);
	return {
		usedSec,
		remainingSec,
		runningFlows: Number.isInteger(runningFlows) && runningFlows >= 0 ? runningFlows : 0,
	};
}

/** True when core sent a finite pool of hours (a bar is worth showing). */
export function hasPool(limits) {
	return Number.isFinite(limits?.poolSec) && limits.poolSec !== null;
}

/** "6h 12m", "12m", "10h", "45s" (under a minute), "0m". */
export function formatHours(totalSec) {
	const s = Math.max(0, Math.round(Number(totalSec) || 0));
	if (s > 0 && s < 60) return `${s}s`;
	const totalMin = Math.round(s / 60);
	const h = Math.floor(totalMin / 60);
	const m = totalMin % 60;
	if (h && m) return `${h}h ${String(m).padStart(2, '0')}m`;
	if (h) return `${h}h`;
	return `${m}m`;
}

/** "10 hours", "1 hour", "30 minutes" for sentences ("You've used all 10 hours"). */
export function describeHours(totalSec) {
	const s = Math.max(0, Math.round(Number(totalSec) || 0));
	const h = s / 3600;
	if (h >= 1 && Number.isInteger(h)) return `${h} ${h === 1 ? 'hour' : 'hours'}`;
	if (h >= 1) return formatHours(s);
	const m = Math.round(s / 60);
	return `${m} ${m === 1 ? 'minute' : 'minutes'}`;
}

/** "Oct 14" for an epoch-ms cycle end; '' when unknown. */
export function formatResetDate(ms) {
	if (!Number.isFinite(ms) || ms === null) return '';
	try {
		return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	} catch {
		return '';
	}
}

export function notIncluded(limits) {
	return !!limits && limits.included === false && limits.enforced === true;
}

export function poolExhausted(limits, usage) {
	if (!limits?.enforced || !hasPool(limits)) return false;
	const remaining = usage?.remainingSec;
	return Number.isFinite(remaining) && remaining !== null && remaining < POOL_EXHAUSTED_UNDER_SEC;
}

export function poolRatio(limits, usage) {
	if (!hasPool(limits) || limits.poolSec <= 0) return 0;
	return Math.min(1, Math.max(0, (usage?.usedSec ?? 0) / limits.poolSec));
}

export function poolNearlyUsed(limits, usage) {
	return hasPool(limits) && poolRatio(limits, usage) >= POOL_NOTICE_RATIO;
}

/**
 * `runningFlows >= maxConcurrent` while enforced. Callers check it only for a
 * flow that is not itself running (a running flow shows Stop, not Start).
 */
export function concurrencyBlocked(limits, usage) {
	if (!limits?.enforced || !Number.isFinite(limits.maxConcurrent) || limits.maxConcurrent === null) return false;
	return (usage?.runningFlows ?? 0) >= limits.maxConcurrent;
}

/** Tooltip on a control disabled by an exhausted pool. */
export function poolExhaustedTooltip(limits) {
	const reset = formatResetDate(limits?.periodEnd);
	return `You've used all ${describeHours(limits?.poolSec)} this cycle${reset ? ` · resets ${reset}` : ''}`;
}

/** Tooltip on Start when the plan's concurrent-stream cap is reached. */
export function concurrencyTooltip(limits) {
	const n = limits?.maxConcurrent ?? 1;
	return `Your plan runs ${n} captioned stream${n === 1 ? '' : 's'} at a time · stop another to start this one`;
}

/**
 * Header usage chip for `PageHeader :usage`. `null` when the pool is
 * unlimited (no bar). Hours are preformatted; the tooltip explains the cycle.
 */
export function usageBarMeta(limits, usage) {
	if (!hasPool(limits)) return null;
	const used = usage?.usedSec ?? 0;
	const reset = formatResetDate(limits.periodEnd);
	const label = limits.isTrial ? 'trial hours' : '';
	const tooltip = [
		`${formatHours(used)} of ${formatHours(limits.poolSec)} captioning ${limits.isTrial ? 'trial hours' : 'hours'} used this cycle.`,
		reset ? `Resets ${reset}.` : '',
		limits.enforced ? '' : 'Usage is being reported; nothing is blocked yet.',
	].filter(Boolean).join(' ');
	return {
		used,
		limit: limits.poolSec,
		usedLabel: formatHours(used),
		limitLabel: formatHours(limits.poolSec),
		label,
		suffix: reset ? `· resets ${reset}` : '',
		percent: poolRatio(limits, usage),
		tooltip,
		planLabel: 'captioning hours',
	};
}

/**
 * Duration presets for the next window, given the plan.
 *
 *   cap = min(maxWindowSec ?? ∞, poolRemainingSec ?? ∞)   (only while enforced)
 *
 *  - numeric presets above `cap` are dropped;
 *  - a finite `maxWindowSec` that is the binding cap becomes the top preset,
 *    labelled "(plan max)" (relabelling the matching preset, or appended when
 *    it is not one of the standard values); there is no unlimited option;
 *  - otherwise a finite pool replaces "Unlimited" with "Rest of my hours (…)",
 *    which still sends `durationSec: null` — core clamps it to what is left;
 *  - under a minute of pool left (the pool counts as used up) there is
 *    nothing to offer: an empty list, never "Rest of my hours (0m)";
 *  - no plan (or reporting mode): the standard presets, unchanged.
 */
export function durationPresetsFor(limits, poolRemainingSec) {
	const base = DURATION_PRESETS.map((p) => ({ ...p }));
	if (!limits?.enforced) return base;
	const pool = hasPool(limits) && Number.isFinite(poolRemainingSec) && poolRemainingSec !== null ? Math.max(0, poolRemainingSec) : null;
	if (pool !== null && pool < POOL_EXHAUSTED_UNDER_SEC) return [];
	const windowMax = Number.isFinite(limits.maxWindowSec) && limits.maxWindowSec !== null ? limits.maxWindowSec : null;
	if (pool === null && windowMax === null) return base;

	const windowBinds = windowMax !== null && (pool === null || windowMax <= pool);
	const cap = windowBinds ? windowMax : pool;
	const numeric = base.filter((p) => p.value !== null && p.value <= cap);

	if (windowBinds) {
		const exact = numeric.find((p) => p.value === windowMax);
		if (exact) exact.label = `${exact.label} (plan max)`;
		else numeric.push({ value: windowMax, label: `${formatHours(windowMax)} (plan max)` });
		return numeric;
	}
	numeric.push({ value: null, label: `Rest of my hours (${formatHours(pool)})` });
	return numeric;
}

/**
 * Extend presets still allowed for a running window: each must fit in what is
 * left of the pool and keep the window under `maxWindowSec`. Unfiltered while
 * not enforced.
 */
export function extendPresetsFor(limits, poolRemainingSec, windowDurationSec) {
	const base = EXTEND_PRESETS.map((p) => ({ ...p }));
	if (!limits?.enforced) return base;
	const pool = hasPool(limits) && Number.isFinite(poolRemainingSec) && poolRemainingSec !== null ? Math.max(0, poolRemainingSec) : null;
	const windowMax = Number.isFinite(limits.maxWindowSec) && limits.maxWindowSec !== null ? limits.maxWindowSec : null;
	const current = Number.isFinite(windowDurationSec) && windowDurationSec !== null ? windowDurationSec : 0;
	return base.filter((p) => {
		if (pool !== null && p.value > pool) return false;
		if (windowMax !== null && current + p.value > windowMax) return false;
		return true;
	});
}

/** Countdown copy when the pool ends before the window ("Hours run out in 41:59"). */
export function hoursRunOutLabel(sec) {
	return `Hours run out in ${formatCountdown(sec)}`;
}

export const LIMIT_ERROR_CODES = Object.freeze([
	'captions_not_included',
	'captions_too_many_languages',
	'captions_concurrency_limit',
	'captions_pool_exhausted',
	'captions_window_too_long',
	'captions_rate_limited',
]);

export function isLimitError(err) {
	return !!err && LIMIT_ERROR_CODES.includes(err.code);
}

/**
 * Publisher-facing copy for a limit error from core, or `null` when the error
 * is not one. `{ title, message, upgrade }`; `upgrade` says whether to offer
 * the plans page next to the message.
 */
export function limitErrorCopy(err) {
	if (!isLimitError(err)) return null;
	const data = err.data && typeof err.data === 'object' ? err.data : {};
	switch (err.code) {
		case 'captions_not_included':
			return {
				title: 'Captioning isn\'t included in your plan',
				message: 'Captioning isn\'t included in your current plan. Upgrade to add live captions and translations to your streams.',
				upgrade: true,
			};
		case 'captions_too_many_languages': {
			const n = positiveOrNull(data.maxLangs) ?? 1;
			return {
				title: 'Too many languages for your plan',
				message: `Your plan includes ${n} ${n === 1 ? 'language' : 'languages'} per flow. Remove ${n === 1 ? 'the extra languages' : 'a language'} or upgrade for more.`,
				upgrade: true,
			};
		}
		case 'captions_concurrency_limit': {
			const n = positiveOrNull(data.maxConcurrent) ?? 1;
			return {
				title: 'Another stream is already being captioned',
				message: `Your plan runs ${n} captioned stream${n === 1 ? '' : 's'} at a time. Stop captions on another stream to start this one, or upgrade to run more.`,
				upgrade: true,
			};
		}
		case 'captions_pool_exhausted': {
			const remaining = nonNegativeOr(data.remainingSec, null);
			const reset = formatResetDate(epochOrNull(data.periodEnd));
			const resets = reset ? ` They reset ${reset}.` : '';
			if (remaining !== null && remaining >= POOL_EXHAUSTED_UNDER_SEC) {
				return {
					title: 'Not enough captioning hours left',
					message: `Only ${formatHours(remaining)} of captioning hours are left this cycle.${resets} Pick a shorter duration or upgrade for more hours.`,
					upgrade: true,
				};
			}
			return {
				title: 'Captioning hours used up',
				message: `You've used all your captioning hours this cycle.${resets} Upgrade for more hours.`,
				upgrade: true,
			};
		}
		case 'captions_window_too_long': {
			const max = positiveOrNull(data.maxSec);
			return {
				title: 'Window longer than your plan allows',
				message: max
					? `Your plan caps each caption window at ${formatHours(max)}. Pick a shorter duration or upgrade for longer windows.`
					: 'That window is longer than your plan allows. Pick a shorter duration or upgrade for longer windows.',
				upgrade: true,
			};
		}
		case 'captions_rate_limited':
			return {
				title: 'Too many changes at once',
				message: 'You\'ve made a lot of captioning changes in a short time. Wait a minute and try again.',
				upgrade: false,
			};
		default:
			return null;
	}
}

/**
 * Folds the numbers a limit error carries (`remainingSec`, `periodEnd`,
 * `maxLangs`, `maxConcurrent`, `maxSec`) back into limits/usage so the
 * controls reflect what core just said without another round-trip.
 */
export function applyLimitErrorData(err, limits, usage) {
	const data = err?.data && typeof err.data === 'object' ? err.data : null;
	if (!data) return { limits, usage };
	const nextLimits = { ...limits };
	const nextUsage = { ...usage };
	if (err.code === 'captions_pool_exhausted') {
		const remaining = nonNegativeOr(data.remainingSec, null);
		if (remaining !== null) {
			nextUsage.remainingSec = remaining;
			if (hasPool(nextLimits)) nextUsage.usedSec = Math.max(nextUsage.usedSec, nextLimits.poolSec - remaining);
		}
		const end = epochOrNull(data.periodEnd);
		if (end) nextLimits.periodEnd = end;
	}
	if (err.code === 'captions_too_many_languages' && positiveOrNull(data.maxLangs)) nextLimits.maxLangs = Number(data.maxLangs);
	if (err.code === 'captions_concurrency_limit' && positiveOrNull(data.maxConcurrent)) nextLimits.maxConcurrent = Number(data.maxConcurrent);
	if (err.code === 'captions_window_too_long' && positiveOrNull(data.maxSec)) nextLimits.maxWindowSec = Number(data.maxSec);
	if (err.code === 'captions_not_included') {
		nextLimits.included = false;
		nextLimits.enforced = true;
	}
	if (err.code !== 'captions_rate_limited') nextLimits.enforced = true;
	return { limits: nextLimits, usage: nextUsage };
}
